import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import OriginalGalleryView from '../components/Gallery/OriginalGalleryView';
import BlogSection from '../components/Blog/BlogSection';
import PackagesGrid from '../components/Services/PackagesGrid';
import PackageModal from '../components/Services/ServiceModal';
import InstagramFeed from '../components/Instagram/InstagramFeed';
import PackageFinderWizard from '../components/Packages/PackageFinderWizard';
import SEOHead from '../components/SEO/SEOHead';
import { Package } from '../types/Package';
import { loadServicesFromServer } from '../utils/ServiceAPI';
import { loadCategories } from '../utils/categories';
import { parsePackageFinderParams } from '../utils/packageFinderShare';
import HomeAboutSection from '../components/Home/HomeAboutSection';
import { readSearchBootstrap } from '../components/Home/SearchPhotoStrip';
import WhereIShoot from '../components/Home/WhereIShoot';
import { LOCAL_BUSINESS, whatsappHref } from '../utils/localBusiness';
import categoryContent from '../utils/categoryContent';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import InquiryDatePicker from '../components/Common/InquiryDatePicker';
import { useInquiryDate } from '../utils/inquiryDate';
import { preferredDateLine } from '../utils/preferredDate';
import './HomePage.css';

function HomeSectionMark() {
  return (
    <div className="home-section-mark" aria-hidden="true">
      <span />
      <img src={`${process.env.PUBLIC_URL || ''}/carmen-logo.png`} alt="" />
      <span />
    </div>
  );
}

const HomePage: React.FC = () => {
  const location = useLocation();
  const websiteContent = useWebsiteContent();
  const [inquiryDate] = useInquiryDate(false);
  const [homePage, setHomePage] = useState(() => {
    const published = readSearchBootstrap('home');
    return categoryContent.resolveHomePage(published && published.homePage ? published.homePage : null);
  });
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardCategoryFilter, setWizardCategoryFilter] = useState<string | null>(null);
  const [currentPackageIndex, setCurrentPackageIndex] = useState<number>(0);
  const [categories, setCategories] = useState<Array<{ slug: string; name: string }>>([]);
  const displaySettings = useDisplaySettings();
  const homeSearch = readSearchBootstrap('home');
  const shareImage = homeSearch?.shareImage;

  // Handle URL parameters and hash navigation on mount
  useEffect(() => {
    // Check for Package Finder URL parameters
    const params = parsePackageFinderParams();
    if (params.open) {
      // Open the wizard after a short delay to ensure page is loaded
      setTimeout(() => {
        setIsWizardOpen(true);
      }, 500);
    }

    if (!params.open && !window.location.hash) {
      // No hash and no package finder - ensure page is at top (galleries section visible)
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadHomepage = () => {
      fetch('/api/homepage')
        .then((response) => (response.ok ? response.json() : null))
        .then((page) => {
          if (!cancelled && page) setHomePage(categoryContent.resolveHomePage(page));
        })
        .catch(() => {});
    };
    loadHomepage();
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') loadHomepage();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return;

    const alignSection = () => {
      const element = document.getElementById(hash);
      if (!element) return;
      const nav = document.querySelector('.navbar');
      const navHeight = nav ? Math.ceil(nav.getBoundingClientRect().height) : 70;
      const target = hash === 'contact' ? (element.querySelector('h1, h2') || element) : element;
      const clearance = hash === 'contact' ? 16 : 12;
      const top = target.getBoundingClientRect().top + window.scrollY - navHeight - clearance;
      if (Math.abs(window.scrollY - top) < 8) return;
      window.scrollTo({ top, behavior: 'auto' });
    };

    const first = window.setTimeout(alignSection, 100);
    const followUp = hash === 'instagram' ? window.setTimeout(alignSection, 700) : undefined;
    return () => {
      window.clearTimeout(first);
      if (followUp) window.clearTimeout(followUp);
    };
  }, [location.hash]);

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const packagesData = await loadServicesFromServer();
        setPackages(packagesData);
      } catch (error) {
        console.error('Error loading packages:', error);
      }
    };

    loadPackages();

    // Load categories
    const loadCategoriesData = () => {
      try {
        const allCategories = loadCategories();
        setCategories(allCategories.map(cat => ({ slug: cat.slug, name: cat.name })));
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategoriesData();

    const handleCurrencySettingsUpdate = () => {
      loadPackages();
    };

    window.addEventListener('currency_settings_updated', handleCurrencySettingsUpdate);

    return () => {
      window.removeEventListener('currency_settings_updated', handleCurrencySettingsUpdate);
    };
  }, []);


  // Filter packages to show only featured packages
  const featuredPackages = useMemo(() => {
    return packages.filter(p => p.featured && p.available);
  }, [packages]);


  const handlePackageClick = React.useCallback((packageItem: Package, categoryFilter?: string | null) => {
    // Set the wizard category filter if provided
    if (categoryFilter) {
      setWizardCategoryFilter(categoryFilter);
    } else {
      setWizardCategoryFilter(null);
    }
    
    setSelectedPackage(packageItem);
    setIsPackageModalOpen(true);
  }, []);

  // Get packages for modal (filtered by wizard category if applicable, otherwise only featured packages)
  const modalPackages = useMemo(() => {
    if (wizardCategoryFilter) {
      const categoryFilterLower = wizardCategoryFilter.toLowerCase().trim();
      
      // Find matching category from categories list to get both name and slug
      const matchingCategory = categories.find(cat => 
        cat.slug.toLowerCase().trim() === categoryFilterLower ||
        cat.name.toLowerCase().trim() === categoryFilterLower
      );
      
      return packages.filter(p => {
        if (!p.category && (!p.categories || p.categories.length === 0)) return false;
        
        // Check categories array
        if (p.categories && p.categories.length > 0) {
          return p.categories.some(catSlug => {
            const catSlugLower = catSlug.toLowerCase().trim();
            return catSlugLower === categoryFilterLower ||
                   (matchingCategory && catSlugLower === matchingCategory.slug.toLowerCase().trim()) ||
                   (matchingCategory && catSlugLower === matchingCategory.name.toLowerCase().trim());
          });
        }
        
        // Check single category field
        if (p.category) {
          const pCategoryLower = p.category.toLowerCase().trim();
          return pCategoryLower === categoryFilterLower ||
                 (matchingCategory && pCategoryLower === matchingCategory.slug.toLowerCase().trim()) ||
                 (matchingCategory && pCategoryLower === matchingCategory.name.toLowerCase().trim());
        }
        
        return false;
      });
    }
    // When clicking from homepage (not from wizard), only show featured packages
    return featuredPackages;
  }, [wizardCategoryFilter, packages, categories, featuredPackages]);

  // Update current index when package is selected
  useEffect(() => {
    if (selectedPackage && modalPackages.length > 0) {
      const index = modalPackages.findIndex(p => p.id === selectedPackage.id);
      if (index >= 0) {
        setCurrentPackageIndex(index);
      }
    }
  }, [selectedPackage, modalPackages]);

  const handleNavigateUpdated = (index: number) => {
    if (index >= 0 && index < modalPackages.length) {
      setCurrentPackageIndex(index);
      setSelectedPackage(modalPackages[index]);
    }
  };

  const closePackageModal = () => {
    setIsPackageModalOpen(false);
    setSelectedPackage(null);
    setWizardCategoryFilter(null); // Clear wizard filter when modal closes
  };

  return (
    <div className="home-page" data-route="/" data-track-source="home">
      <SEOHead
        exactTitle
        title={homePage.title}
        description={homePage.description}
        keywords={['photography', 'portraits', 'weddings', 'events', 'professional photographer', 'Cape Town', 'lifestyle photography']}
        image={shareImage?.url || undefined}
        imageAlt={shareImage?.alt || undefined}
      />
      <main className="page-content">
        <section className="home-masthead" aria-label="Introduction">
          <div className="galleries-container">
            <div className="galleries-header">
              <div className="header-content">
                <h1 className="galleries-title">{homePage.h1}</h1>
              </div>
            </div>
            <div
              className="galleries-intro"
              dangerouslySetInnerHTML={{ __html: categoryContent.renderHomeIntroHtml(homePage.intro) }}
            />
          </div>
        </section>
        {(displaySettings?.showGalleriesSection !== false) && (
          <section id="galleries" className="galleries-section">
            <div className="galleries-container">
              <OriginalGalleryView />
            </div>
          </section>
        )}
      </main>
      
      {/* About Section - Moved to appear between Galleries and Packages */}
      {(displaySettings?.showAboutSection !== false) && (
        <>
          <HomeAboutSection content={websiteContent} sectionId="about" />
          <WhereIShoot />
        </>
      )}

      {/* Packages Section - Featured Only - Now appears after About */}
      {(displaySettings?.showServicesSection !== false) && featuredPackages.length > 0 && (
        <section id="services">
          <PackagesGrid 
            packages={featuredPackages.sort((a, b) => (a.order || 999) - (b.order || 999))}
            onPackageClick={handlePackageClick}
            onPackageFinderClick={() => setIsWizardOpen(true)}
            maxItems={displaySettings?.featuredPackageCount ?? 3}
            key={`packages-${displaySettings?.featuredPackageCount ?? 3}-${Date.now()}`}
          />
        </section>
      )}

      <HomeSectionMark />

      {/* Instagram Feed Section - Appears after Packages */}
      {(displaySettings?.showInstagramSection !== false) && (
      <section id="instagram" className="instagram-section">
        <div className="instagram-section-container">
          <div className="instagram-section-header">
            <h2 className="elegant-heading">Instagram</h2>
          </div>
          <InstagramFeed showCaptions showStats className="homepage-instagram-feed" />
          <div className="instagram-section-footer">
            <a
              href="https://www.instagram.com/carmenlorrainephotography/"
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-profile-link"
            >
              @carmenlorrainephotography
            </a>
          </div>
        </div>
      </section>
      )}

      <HomeSectionMark />

      {/* Package Modal */}
      <PackageModal 
        service={selectedPackage}
        isOpen={isPackageModalOpen}
        onClose={closePackageModal}
        allPackages={modalPackages}
        currentIndex={currentPackageIndex}
        onNavigate={handleNavigateUpdated}
      />

      {/* Package Finder Wizard */}
      <PackageFinderWizard
        packages={packages}
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onPackageSelect={handlePackageClick}
      />

      {/* Blog Section */}
      {(displaySettings?.showBlogSection !== false) && (
        <section id="blog" className="blog-section">
          <BlogSection variant="home" />
        </section>
      )}

      {/* Contact Section - Moved to bottom */}
      {(displaySettings?.showContactSection !== false) && (
        <section id="contact" className="contact-section public-contact">
        <div className="contact-content">
          <h2 className="elegant-heading">Let's Create Something Beautiful Together</h2>
          <p className="contact-intro">Ready to capture your story? Get in touch to discuss your photography needs.</p>
          <InquiryDatePicker />
          <ul className="contact-details">
            <li className="contact-row">
              <i className="fas fa-envelope" aria-hidden="true"></i>
              <a 
                href={`mailto:${LOCAL_BUSINESS.email}?subject=Photography Inquiry - ${websiteContent.studioName}&body=Hi Carmen,%0D%0A%0D%0AI found your website and I'm interested in your photography services.%0D%0A%0D%0ACould you please provide more information about:%0D%0A- Your packages and pricing%0D%0A- Available dates%0D%0A- What's included in your services%0D%0A%0D%0A${encodeURIComponent(preferredDateLine(inquiryDate))}%0D%0A%0D%0AThank you!%0D%0A%0D%0ABest regards,`}
                className="contact-email"
              >
                {LOCAL_BUSINESS.email}
              </a>
            </li>
            <li className="contact-row">
              <i className="fas fa-phone" aria-hidden="true"></i>
              <a 
                href={LOCAL_BUSINESS.phoneTel}
                className="contact-phone"
              >
                {LOCAL_BUSINESS.phoneDisplay}
              </a>
            </li>
            <li className="contact-row">
              <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
              <span className="contact-location">{websiteContent.contactLocation}</span>
            </li>
          </ul>
          <div className="contact-actions">
            <a
              href={whatsappHref(LOCAL_BUSINESS.telephone)}
              className="contact-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
            >
                <i className="fab fa-whatsapp" aria-hidden="true"></i>
                <span>Message on WhatsApp</span>
              </a>
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default HomePage;
